import type { LucideIcon } from 'lucide-react';

import { Derived, Effect, Store } from '@tanstack/react-store';
import { Cloud, CloudAlert, CloudDownload, CloudUpload } from 'lucide-react';

export type SynchronizationStatusType = 'up-to-date' | 'syncing:up' | 'syncing:down' | 'error';

export type SynchronizationState = {
    synchronizationStatus: SynchronizationStatusType;
    lastSynced: Date | null;
    sheetOpen: boolean;
};

export const SYNCHRONIZATION_STATUS_ICONS = {
    'up-to-date': Cloud,
    'syncing:up': CloudUpload,
    'syncing:down': CloudDownload,
    'error': CloudAlert,
} as const satisfies Record<SynchronizationStatusType, LucideIcon>;

export const syncStatusStore = new Store<SynchronizationState>({
    synchronizationStatus: 'up-to-date',
    lastSynced: null,
    sheetOpen: false,
});

export const isUpToDate = new Derived<boolean>({
    fn: () => syncStatusStore.state.synchronizationStatus === 'up-to-date',
    deps: [syncStatusStore],
});

export const isSyncing = new Derived<[boolean, 'up' | 'down' | null]>({
    fn: () => {
        const { synchronizationStatus: connectionStatus } = syncStatusStore.state;
        if (connectionStatus === 'syncing:up')
            return [true, 'up'];
        if (connectionStatus === 'syncing:down')
            return [true, 'down'];
        return [false, null];
    },
    deps: [syncStatusStore],
});

export const isError = new Derived<boolean>({
    fn: () => syncStatusStore.state.synchronizationStatus === 'error',
    deps: [syncStatusStore],
});

export const lastSynced = new Derived<Date | null>({
    fn: () => syncStatusStore.state.lastSynced,
    deps: [syncStatusStore],
});

export function setSynchronizationStatus(state: SynchronizationStatusType) {
    syncStatusStore.setState(prev => ({
        ...prev,
        synchronizationStatus: state,
    }));
}

export function setSheetState(state: boolean) {
    syncStatusStore.setState(prev => ({
        ...prev,
        sheetOpen: state,
    }));
}

export function toggleSheet() {
    syncStatusStore.setState(prev => ({
        ...prev,
        sheetOpen: !prev.sheetOpen,
    }));
}

export const lastSyncEffect = new Effect({
    fn: () => {
        if (syncStatusStore.state.synchronizationStatus === 'up-to-date') {
            syncStatusStore.setState(prev => ({
                ...prev,
                lastSynced: new Date(),
            }));
        }
    },
    deps: [syncStatusStore],
});
